import { type KeyboardEvent } from "react";
import { ChevronIcon } from "vega-icons";

import { Key } from "@vega-core/constants/global";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isEmbeddableVideoUrl } from "@vega-core/utils/url.helper";

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../shadcn/card";

type Props = {
  title: string;
  description?: string;
  imageUrl?: string;
  videoUrl?: string;
  readTime?: string;
  actionLabel?: string;
  className?: string;
  ariaLabel?: string;
  onClick?: () => void;
};

export const ResourceArticleCard = ({
  actionLabel,
  ariaLabel,
  className,
  description,
  imageUrl,
  videoUrl,
  onClick,
  readTime,
  title,
}: Props) => {
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!onClick) {
      return;
    }

    if (event.key === Key.Enter || event.key === " ") {
      event.preventDefault();
      onClick();
    }
  };

  const renderPreviewMedia = () => {
    if (videoUrl) {
      return isEmbeddableVideoUrl(videoUrl) ? (
        <iframe
          className="pointer-events-none absolute inset-0 h-full w-full"
          src={videoUrl}
          title={title}
        />
      ) : (
        <video
          className="absolute inset-0 h-full w-full object-cover"
          muted
          playsInline
          preload="metadata"
          src={videoUrl}
        />
      );
    }

    if (imageUrl) {
      return (
        <img
          alt={title}
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
          src={imageUrl}
        />
      );
    }

    return null;
  };

  return (
    <Card
      aria-label={onClick ? (ariaLabel ?? title) : undefined}
      className={cnTwMerge(
        "group flex h-full flex-col gap-0 overflow-hidden pt-0 pb-0 transition-colors",
        onClick &&
          "cursor-pointer hover:border-brand-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-default focus-visible:ring-offset-2",
        className,
      )}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <CardContent className="p-0">
        <div className="relative h-44 bg-background/secondary">
          {renderPreviewMedia()}
        </div>
      </CardContent>
      <CardHeader className="p-3 gap-2">
        <CardTitle className="body-base-semibold text-text/primary leading-snug line-clamp-2">
          {title}
        </CardTitle>
        {description ? (
          <CardDescription className="body-small-regular text-text/secondary leading-relaxed line-clamp-2">
            {description}
          </CardDescription>
        ) : null}
      </CardHeader>
      <CardFooter className="mt-auto justify-between border-t border-border/secondary p-3 pt-3">
        {readTime ? (
          <span className="body-small-medium text-text/interactive bg-background/brand/opacity px-2 py-1 rounded-default">
            {readTime}
          </span>
        ) : null}
        {actionLabel ? (
          <span className="ml-auto inline-flex items-center body-small-medium text-text/on-brand bg-brand-default px-3 py-1.5 rounded-default transition-opacity opacity-0 group-hover:opacity-100 group-focus-within:opacity-100">
            {actionLabel}
            <ChevronIcon className="ml-1 w-3 h-3 -rotate-90" />
          </span>
        ) : null}
      </CardFooter>
    </Card>
  );
};
